import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, ConfigProvider, Flex, Form, Input, InputNumber, Modal, Popover, Radio, Segmented, Select, Switch, Table, TableProps, Tabs, Typography } from "antd";
import { DeleteOutlined, EditOutlined, HistoryOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageHeading from "components/PageHeading.component";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { th, tnum } from "shared/components/TableText";
import { commissionStructureLabel, formatCurrency, PAYROLL_RATE_STATUS_LABELS, PAYROLL_RATE_STATUS_TONE } from "shared/utils";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { showDestructiveConfirm } from "components/modals/destructiveConfirm";
import { showConfirmModal } from "components/modals/confirmModal";
import { pillSegmentedTheme } from "pages/analytics/RangeSegmented";
import { StatusBadge, ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { ClassCategory, CommissionMode, CommissionRateStructure, PayrollItemKind, PayrollRunStatus, UserType, karyawanTypeOf } from "shared/types/enum";
import { DraftApplyChoice, PayrollClassSummary, PayrollRecurringItem, PayrollStaffOption } from "shared/types";
import { PAGE_SIZE } from "shared/constants";
import { getTextSearchProps } from "shared/components/SearchColumnProps";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import {
  payrollSettingsOptions,
  payrollClassesOptions,
  payrollRecurringItemsOptions,
  payrollRunsOptions,
  payrollStaffOptions,
} from "hooks/queries/payroll";
import {
  useCreatePayrollRecurringItem,
  useUpdatePayrollRecurringItem,
  useDeletePayrollRecurringItem,
  useUpdateStaffBaseSalary,
} from "hooks/mutations/payroll";
import { currencyInputProps } from "./currencyInput";
import { textPx } from "shared/constants/zoom";
import { colors } from "shared/theme/color";
import { useAuth } from "context/AuthContextProvider";
import FrequencyForm from "./FrequencyForm";
import PayrollSetupGuide, { PAYROLL_CLASSES_CARD_ID, PAYROLL_STAFF_CARD_ID, usePayrollGuideHidden } from "./PayrollSetupGuide";

// The regular Pengaturan Penggajian card, shown once the setup guide is gone
// (the first payroll run exists, or the Manager hid the guide). While the
// guide shows, its first step is the frequency control instead.
// `onShowGuide` brings a hidden guide back; passed only while no payroll run
// exists yet.
const SettingsCard = ({ onShowGuide }: { onShowGuide?: () => void }) => (
  <Card
    title={translate("payrollPage.settings.card_title")}
    extra={
      onShowGuide ? (
        <Button type="link" style={{ padding: 0, height: "auto", color: colors.navy }} onClick={onShowGuide}>
          {translate("payrollPage.guide.show")}
        </Button>
      ) : undefined
    }
    styles={{ body: { padding: 18 } }}
  >
    <FrequencyForm layout="stacked" />
  </Card>
);

// The only marker a locked card carries: a lock icon in the Card head, no
// badge, no text and no per-control tooltip (the house rule for disabled
// in-context actions, see components/capability). The setup guide above,
// which is always shown while the cards are locked, says what unlocks them.
const LockedHint = () => (
  <LockOutlined aria-label={translate("payrollPage.guide.unlock_after_step1")} style={{ color: colors.ink2, fontSize: textPx(14) }} />
);

// Card styles while locked: the body is dimmed with a not-allowed cursor so
// the tables read as unavailable rather than as live content to fix.
const lockedCardStyles = {
  body: { padding: 18, opacity: 0.5, cursor: "not-allowed" as const },
};

// Short label for the class list's "Struktur" column — mirrors the same
// structure names used on the class commission page itself. Per Kelas is
// the only structure with no sub-choice (always flat), so it alone renders
// with no parenthetical — Per Peserta and Per Pendapatan each have two
// sub-choices, so both get the "(sub-choice)" treatment for symmetry.
const structureLabel = commissionStructureLabel;

// Class-first: pick a class, configure its ONE commission structure, then
// set per-instructor values on the class's own page — replaces the old
// instructor-first list, which made double-paying the same class
// structurally possible.
// `locked` = the studio hasn't saved Pengaturan Penggajian yet (no
// payroll_setting row), so nothing under this card can be written: the
// server refuses every class-commission write until the row exists. The
// table stays visible but read-only. Row navigation is neutered by hand
// because AntD's DisabledContext never reaches a Table onRow click (see
// DiscountCodesCard for the same caveat).
const ClassesCard = ({ locked = false }: { locked?: boolean }) => {
  const navigate = useNavigate();
  const { data: classes = [], isLoading } = useQuery(payrollClassesOptions());
  const { page, handleTableChange } = useRememberedTablePage("payroll-classes");
  // Default order: Private classes first, then Open, A to Z inside each group
  // (owner's call: one mixed alphabet read as messy). Column sorts and
  // filters still apply on top of this.
  const orderedClasses = useMemo(
    () =>
      [...classes].sort(
        (a, b) =>
          (a.category === ClassCategory.Private ? 0 : 1) - (b.category === ClassCategory.Private ? 0 : 1) ||
          a.name.localeCompare(b.name),
      ),
    [classes],
  );

  const columns: TableProps<PayrollClassSummary>["columns"] = [
    {
      title: th(translate("payrollPage.rates.col_class_name")),
      dataIndex: "name",
      // Spread before the render so the column's own render (badges and
      // styling, not the raw value) is the one the Table sees.
      ...getTextSearchProps<PayrollClassSummary, "name">("name"),
      render: (_, record) => <Typography.Text style={{ fontWeight: 600 }}>{record.name}</Typography.Text>,
    },
    {
      // Native AntD filters/onFilter (funnel icon + checkbox list), not the
      // shared getEnumSearchProps helper — that helper always renders a
      // magnifying-glass search icon (a single-value Select dropdown) even
      // for enum columns, which reads as "search" rather than "filter" for
      // a column with only two possible values. Left the shared helper
      // itself untouched since other tables may want its search behavior.
      title: th(translate("payrollPage.rates.col_category")),
      dataIndex: "category",
      // Plain literal text, matching the Kelas page's own "Jenis Kelas"
      // filter (pages/products/classtype/index.tsx) exactly — ClassTypeBadge
      // itself isn't translated either (just capitalizes the raw enum
      // value), so there's no real "Private"/"Open" i18n key to call. The
      // previous version of this filter called translate() on
      // payrollPage.rates.category_open/category_private, keys that don't
      // exist in either language file — translate() falls back to printing
      // the raw key path, which is what showed up literally in the dropdown.
      filters: [
        { value: ClassCategory.Private, text: "Private" },
        { value: ClassCategory.Open, text: "Open" },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category) => <ClassTypeBadge type={category} />,
    },
    {
      title: th(translate("payrollPage.rates.col_status")),
      dataIndex: "configured",
      // String values, never booleans: AntD round-trips a picked filter as a
      // string key, so `true`/`false` stopped matching after the first click
      // (the same trap the Klien / Instruktur Active filters had).
      filters: [
        { text: PAYROLL_RATE_STATUS_LABELS.configured, value: "configured" },
        { text: PAYROLL_RATE_STATUS_LABELS.unconfigured, value: "unconfigured" },
      ],
      onFilter: (value, record) => (record.configured ? "configured" : "unconfigured") === value,
      // Grey while locked: an amber "Not Configured" on a row nobody can open
      // yet read as a problem to fix now.
      render: (configured: boolean) =>
        configured ? (
          <StatusBadge tone={PAYROLL_RATE_STATUS_TONE.configured} label={PAYROLL_RATE_STATUS_LABELS.configured} />
        ) : (
          <StatusBadge tone={locked ? "neutral" : PAYROLL_RATE_STATUS_TONE.unconfigured} label={PAYROLL_RATE_STATUS_LABELS.unconfigured} />
        ),
    },
    {
      // Filters on the underlying mode/rate_structure enums, not a
      // string-match against structureLabel()'s rendered text — robust to
      // the label wording changing later. Seven options total (six
      // configured combinations + unconfigured) since that's the complete,
      // fixed set structureLabel can ever produce; no need to collapse to
      // just the three top-level modes, the Flat/Tiered/Persentase/Persentase
      // Bertingkat distinction is exactly what someone auditing consistency
      // wants.
      title: th(translate("payrollPage.rates.col_structure")),
      key: "structure",
      filters: [
        { text: translate("payrollPage.rates.mode_per_class"), value: "per_class" },
        { text: `${translate("payrollPage.rates.mode_per_attendee")} (${translate("payrollPage.rates.structure_flat")})`, value: "per_attendee_flat" },
        { text: `${translate("payrollPage.rates.mode_per_attendee")} (${translate("payrollPage.rates.structure_tiered")})`, value: "per_attendee_tiered" },
        { text: `${translate("payrollPage.rates.mode_revenue")} (${translate("payrollPage.rates.structure_revenue_percent")})`, value: "revenue_percent" },
        { text: `${translate("payrollPage.rates.mode_revenue")} (${translate("payrollPage.rates.structure_tiered")})`, value: "revenue_tiered" },
        {
          text: `${translate("payrollPage.rates.mode_revenue")} (${translate("payrollPage.rates.structure_revenue_percent_tiered")})`,
          value: "revenue_percent_tiered",
        },
        { text: PAYROLL_RATE_STATUS_LABELS.unconfigured, value: "unconfigured" },
      ],
      onFilter: (value, record) => {
        switch (value) {
          case "unconfigured":
            return !record.mode || !record.rate_structure;
          case "per_class":
            return record.mode === CommissionMode.PerClass;
          case "per_attendee_flat":
            return record.mode === CommissionMode.PerAttendee && record.rate_structure === CommissionRateStructure.Flat;
          case "per_attendee_tiered":
            return record.mode === CommissionMode.PerAttendee && record.rate_structure === CommissionRateStructure.Tiered;
          case "revenue_percent":
            return record.rate_structure === CommissionRateStructure.RevenuePercent;
          case "revenue_tiered":
            return record.rate_structure === CommissionRateStructure.RevenueTiered;
          case "revenue_percent_tiered":
            return record.rate_structure === CommissionRateStructure.RevenuePercentTiered;
          default:
            return false;
        }
      },
      render: (_, record) => structureLabel(record.mode, record.rate_structure),
    },
    {
      title: th(translate("payrollPage.rates.col_instructor_count")),
      dataIndex: "instructor_count",
      sorter: (a, b) => a.instructor_count - b.instructor_count,
      render: (count: number) => <span style={tnum}>{count}</span>,
    },
  ];

  return (
    <Card
      id={PAYROLL_CLASSES_CARD_ID}
      title={translate("payrollPage.rates.card_title")}
      extra={locked ? <LockedHint /> : undefined}
      styles={locked ? lockedCardStyles : { body: { padding: 18 } }}
    >
      {/* `locked || undefined` (not a plain boolean): AntD only installs a
          DisabledContext when the prop is defined, so an unlocked card
          inherits any ancestor's disabled state instead of overriding it. */}
      <ConfigProvider componentDisabled={locked || undefined}>
        <Table
          showSorterTooltip={false}
          rowKey="class_type_id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={orderedClasses}
          onRow={
            locked
              ? undefined
              : (record) => ({
                  onClick: () => navigate(`${PATHS.PAYROLL_CLASS_RATE}/${record.class_type_id}`),
                  style: { cursor: "pointer" },
                })
          }
          pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false }}
          onChange={handleTableChange}
        />
      </ConfigProvider>
    </Card>
  );
};

// "Berlaku mulai": shown on every Gaji Pokok / Item Berulang write while a
// payroll DRAFT is open. "Periode ini" writes the change onto the draft now
// (this is the period being paid); "Periode berikutnya" leaves the draft's
// number alone and the change feeds the next draft. With no open draft the
// picker is not rendered and the writes behave as before.
// value/onChange are optional so AntD's Form.Item can inject them.
const ApplyToPicker = ({ value = "current", onChange }: { value?: DraftApplyChoice; onChange?: (v: DraftApplyChoice) => void }) => (
  <Flex vertical gap={8}>
    <Typography.Text style={{ fontSize: textPx(13), fontWeight: 500 }}>{translate("payrollPage.applyTo.label")}</Typography.Text>
    {/* Sized to its labels, not stretched to the container: a full-width
        pill pair read as a second input box. */}
    <ConfigProvider theme={pillSegmentedTheme}>
      <Segmented
        style={{ alignSelf: "flex-start" }}
        value={value}
        onChange={(v) => onChange?.(v as DraftApplyChoice)}
        options={[
          { label: translate("payrollPage.applyTo.current"), value: "current" },
          { label: translate("payrollPage.applyTo.next"), value: "next" },
        ]}
      />
    </ConfigProvider>
    <Typography.Text type="secondary" style={{ fontSize: textPx(12), lineHeight: 1.4 }}>
      {translate("payrollPage.applyTo.hint")}
    </Typography.Text>
  </Flex>
);

// Self-contained variant for the static delete confirm (no parent state).
const ApplyToPickerUncontrolled = ({ onChange }: { onChange: (v: DraftApplyChoice) => void }) => {
  const [value, setValue] = useState<DraftApplyChoice>("current");
  return (
    <ApplyToPicker
      value={value}
      onChange={(v) => {
        setValue(v);
        onChange(v);
      }}
    />
  );
};

const RecurringItemFormModal = ({
  open,
  editing,
  staff,
  hasOpenDraft,
  onClose,
}: {
  open: boolean;
  editing?: PayrollRecurringItem;
  staff: PayrollStaffOption[];
  hasOpenDraft: boolean;
  onClose: () => void;
}) => {
  const [form] = Form.useForm();
  const { mutate: create, isPending: isCreating } = useCreatePayrollRecurringItem();
  const { mutate: update, isPending: isUpdating } = useUpdatePayrollRecurringItem();

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const onSuccess = () => {
          form.resetFields();
          onClose();
        };
        // apply_to only travels while a draft is open; otherwise the server
        // has nothing to apply it to and the field is left out entirely.
        const { apply_to, ...rest } = values;
        const payload = hasOpenDraft ? { ...rest, apply_to } : rest;
        if (editing) {
          update({ id: editing.id, ...payload }, { onSuccess });
        } else {
          create(payload, { onSuccess });
        }
      })
      .catch(() => {});
  };

  return (
    <Modal
      open={open}
      title={editing ? translate("payrollPage.recurring.edit_title") : translate("payrollPage.recurring.add_title")}
      onCancel={onClose}
      footer={
        <ConfigProvider theme={modalButtonsTheme}>
          <Flex justify="flex-end" gap={10}>
            <Button onClick={onClose}>{translate("button.cancel")}</Button>
            <Button type="primary" loading={isCreating || isUpdating} onClick={handleSubmit}>
              {translate("payrollPage.rates.row_save")}
            </Button>
          </Flex>
        </ConfigProvider>
      }
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ ...(editing ?? { kind: PayrollItemKind.Allowance }), apply_to: "current" }}
      >
        <Form.Item name="staff_id" label={translate("payrollPage.recurring.staff_label")} rules={[{ required: true }]}>
          {/* A Staf is labelled so two namesakes of different types stay apart.
              When editing an item whose person is deactivated (and the
              "Sertakan yang tidak aktif" toggle is off) they are not in the
              roster, so their own option is added; otherwise the select
              would show a bare id. */}
          <Select
            showSearch
            optionFilterProp="label"
            options={[
              ...staff.map((s) => ({
                value: s.user_id,
                label:
                  karyawanTypeOf(s.user.roles) === UserType.Staff
                    ? `${s.user.full_name} (${translate("karyawan_type.staff")})`
                    : s.user.full_name,
              })),
              ...(editing && !staff.some((s) => s.user_id === editing.staff_id)
                ? [{ value: editing.staff_id, label: editing.staff.user.full_name }]
                : []),
            ]}
          />
        </Form.Item>
        <Form.Item name="label" label={translate("payrollPage.recurring.label_field")} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="kind" label={translate("payrollPage.recurring.col_kind")} rules={[{ required: true }]}>
          <Radio.Group>
            <Radio.Button value={PayrollItemKind.Allowance}>{translate("payrollPage.recurring.kind_allowance")}</Radio.Button>
            <Radio.Button value={PayrollItemKind.Deduction}>{translate("payrollPage.recurring.kind_deduction")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="amount" label={translate("payrollPage.recurring.amount_label")} rules={[{ required: true, type: "number", min: 1 }]}>
          <InputNumber {...currencyInputProps} />
        </Form.Item>
        {hasOpenDraft && (
          <Form.Item name="apply_to" style={{ marginBottom: 0 }}>
            <ApplyToPicker />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

// The Gaji Pokok column's inline edit, matching the exact pattern already
// used for basic_salary on the run page itself (SalaryEditForm/Popover in
// PayrollRunPage.tsx) — familiar, not a new interaction to learn. A scoped
// near-duplicate rather than an extraction: different mutation, different
// record shape (PayrollStaffOption vs PayrollLine).
const BaseSalaryEditForm = ({
  staff,
  hasOpenDraft,
  onDone,
}: {
  staff: PayrollStaffOption;
  hasOpenDraft: boolean;
  onDone: () => void;
}) => {
  const [amount, setAmount] = useState<number>(staff.default_basic_salary ?? 0);
  const [applyTo, setApplyTo] = useState<DraftApplyChoice>("current");
  const { mutate, isPending } = useUpdateStaffBaseSalary();
  return (
    // Wider and airier while the picker shows: the amount, the choice and
    // the button each get their own breathing room instead of one stack.
    <Flex vertical gap={hasOpenDraft ? 16 : 8} style={{ width: hasOpenDraft ? 300 : 220, paddingTop: hasOpenDraft ? 4 : 0 }}>
      <InputNumber
        {...currencyInputProps}
        value={amount}
        onChange={(v) => setAmount((v as number) ?? 0)}
        style={{ width: "100%" }}
        autoFocus
      />
      {hasOpenDraft && <ApplyToPicker value={applyTo} onChange={setApplyTo} />}
      <ConfigProvider theme={modalButtonsTheme}>
        <Button
          size="small"
          type="primary"
          loading={isPending}
          onClick={() =>
            mutate({ staffId: staff.user_id, amount, ...(hasOpenDraft ? { apply_to: applyTo } : {}) }, { onSuccess: onDone })
          }
        >
          {translate("payrollPage.baseSalary.save_button")}
        </Button>
      </ConfigProvider>
    </Flex>
  );
};

// One row per eligible instructor, no add/delete — unlike Item Berulang,
// Gaji Pokok is a SINGLETON per instructor, not a free-text list (see the
// schema comment on staff.default_basic_salary).
const BaseSalaryTable = ({
  staff,
  isLoading,
  locked = false,
  hasOpenDraft,
}: {
  staff: PayrollStaffOption[];
  isLoading: boolean;
  locked?: boolean;
  hasOpenDraft: boolean;
}) => {
  const [openFor, setOpenFor] = useState<string | null>(null);
  // Paged at the shared PAGE_SIZE like Komisi per Kelas: a live studio with a
  // dozen-plus instructors was filling the whole screen with this one tab.
  const { page, handleTableChange } = useRememberedTablePage("payroll-base-salary");
  // Default order: Instruktur first, then Staf, A to Z inside each group
  // (same reasoning as the class table above).
  const orderedStaff = useMemo(
    () =>
      [...staff].sort(
        (a, b) =>
          (karyawanTypeOf(a.user.roles) === UserType.Instructor ? 0 : 1) -
            (karyawanTypeOf(b.user.roles) === UserType.Instructor ? 0 : 1) ||
          a.user.full_name.localeCompare(b.user.full_name),
      ),
    [staff],
  );
  // Cross-reference into the OTHER tab: same query RecurringItemsTable
  // already fetches (React Query dedupes the identical queryKey, so this
  // is not a second network call), just counted per staff here so an
  // admin can see someone's full pay setup — Gaji Pokok AND how many
  // Tunjangan/Potongan lines they have — without switching tabs.
  const { data: recurringItems = [] } = useQuery(payrollRecurringItemsOptions());
  const recurringCountByStaff = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of recurringItems) {
      counts.set(item.staff_id, (counts.get(item.staff_id) ?? 0) + 1);
    }
    return counts;
  }, [recurringItems]);

  const columns: TableProps<PayrollStaffOption>["columns"] = [
    {
      // Same borrowed-getTextSearchProps trick RecurringItemsTable's own
      // Instruktur column uses: the generic needs a real, type-valid key to
      // satisfy K extends keyof T ("user_id" here, not the actual nested
      // dataIndex), and onFilter is immediately replaced with one that
      // reads the real nested name — getTextSearchProps only supplies the
      // filterDropdown/filterIcon UI shell.
      title: th(translate("payrollPage.baseSalary.col_staff")),
      dataIndex: ["user", "full_name"],
      ...getTextSearchProps<PayrollStaffOption, "user_id">("user_id"),
      onFilter: (value, record) => record.user.full_name.toLowerCase().includes(String(value).toLowerCase()),
      render: (name: string) => <Typography.Text style={{ fontWeight: 600 }}>{name}</Typography.Text>,
    },
    {
      // Instruktur or Staf, plain text (roles already ride on the roster row),
      // with a native two-value filter. Every column on this table is
      // uncontrolled (no filteredValue anywhere), so a plain `filters` is safe
      // next to the name search above.
      title: th(translate("payrollPage.baseSalary.col_type")),
      key: "karyawan_type",
      filters: [
        { text: translate("karyawan_type.instructor"), value: UserType.Instructor },
        { text: translate("karyawan_type.staff"), value: UserType.Staff },
      ],
      onFilter: (value, record) => karyawanTypeOf(record.user.roles) === value,
      render: (_, record) => translate(`karyawan_type.${karyawanTypeOf(record.user.roles)}`),
    },
    {
      title: th(translate("payrollPage.baseSalary.col_amount")),
      key: "default_basic_salary",
      sorter: (a, b) => (a.default_basic_salary ?? 0) - (b.default_basic_salary ?? 0),
      render: (_, record) => (
        <Popover
          trigger="click"
          // The trigger button is already disabled through the card's
          // DisabledContext while locked; forcing `open` false as well means
          // the edit form can never appear, whatever the browser does with a
          // click on a disabled control.
          open={!locked && openFor === record.user_id}
          onOpenChange={(open) => setOpenFor(open ? record.user_id : null)}
          content={<BaseSalaryEditForm staff={record} hasOpenDraft={hasOpenDraft} onDone={() => setOpenFor(null)} />}
          // Popover doesn't unmount its content on close by default, so
          // BaseSalaryEditForm's own amount state would otherwise survive
          // close/reopen — showing a typed-but-never-saved draft instead of
          // the real current value the next time this row's edit is opened.
          destroyOnHidden
        >
          <Button type="text" size="small" style={{ ...tnum, fontWeight: 400 }}>
            {formatCurrency(record.default_basic_salary ?? 0)}
            <EditOutlined style={{ marginInlineStart: 6 }} />
          </Button>
        </Popover>
      ),
    },
    {
      title: th(translate("payrollPage.recurring.card_title")),
      key: "recurring_count",
      sorter: (a, b) => (recurringCountByStaff.get(a.user_id) ?? 0) - (recurringCountByStaff.get(b.user_id) ?? 0),
      render: (_, record) => {
        const count = recurringCountByStaff.get(record.user_id) ?? 0;
        return (
          <span style={tnum}>
            {count > 0 ? `${count} ${translate("payrollPage.baseSalary.recurring_count_unit")}` : "-"}
          </span>
        );
      },
    },
  ];

  return (
    <Table
      showSorterTooltip={false}
      rowKey="user_id"
      size="middle"
      loading={isLoading}
      columns={columns}
      dataSource={orderedStaff}
      pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false }}
      onChange={handleTableChange}
      // No per-column width overrides, so tableLayout="fixed" splits every
      // column evenly instead of sizing to content (Instruktur was pulling
      // most of the row's width, leaving Gaji Pokok/Item Berulang cramped
      // and the whole table reading unevenly).
      tableLayout="fixed"
    />
  );
};

// Item Berulang's table body only — no Card wrapper or "+ Tambah Item"
// button of its own; both now live on the shared StaffPayrollCard wrapper,
// since the button only makes sense while this tab is active.
const RecurringItemsTable = ({
  onEdit,
  hasOpenDraft,
}: {
  onEdit: (item: PayrollRecurringItem) => void;
  hasOpenDraft: boolean;
}) => {
  const { data: items = [], isLoading } = useQuery(payrollRecurringItemsOptions());
  const { mutate: deleteItem } = useDeletePayrollRecurringItem();
  // Same PAGE_SIZE paging as Gaji Pokok and Komisi per Kelas.
  const { page, handleTableChange } = useRememberedTablePage("payroll-recurring-items");

  const columns: TableProps<PayrollRecurringItem>["columns"] = [
    {
      // Bold — matches the row-identity column convention used everywhere
      // else in payroll (Instruktur on the run table, Nama Kelas on
      // Kelas/Komisi per Kelas, Periode on Riwayat Penggajian).
      //
      // getTextSearchProps("label") is borrowed only for its filterDropdown/
      // filterIcon UI shell — its default onFilter does a plain
      // `record[dataIndex]` lookup, which can't reach a NESTED dataIndex
      // like ["staff","user","full_name"] (it would stringify the array
      // into a bogus key). "label" is just a real, type-valid field to
      // satisfy the generic; the onFilter below immediately replaces it
      // with one that actually reads the nested name.
      title: th(translate("payrollPage.recurring.col_staff")),
      dataIndex: ["staff", "user", "full_name"],
      ...getTextSearchProps<PayrollRecurringItem, "label">("label"),
      onFilter: (value, record) => record.staff.user.full_name.toLowerCase().includes(String(value).toLowerCase()),
      render: (name: string) => <Typography.Text style={{ fontWeight: 600 }}>{name}</Typography.Text>,
    },
    { title: th(translate("payrollPage.recurring.col_label")), dataIndex: "label", ...getTextSearchProps<PayrollRecurringItem, "label">("label") },
    {
      // Native funnel-icon checkbox filter, matching Jenis Kelas/Status on
      // the Komisi per Kelas table — same shape (exactly two fixed values).
      title: th(translate("payrollPage.recurring.col_kind")),
      dataIndex: "kind",
      filters: [
        { text: translate("payrollPage.recurring.kind_allowance"), value: PayrollItemKind.Allowance },
        { text: translate("payrollPage.recurring.kind_deduction"), value: PayrollItemKind.Deduction },
      ],
      onFilter: (value, record) => record.kind === value,
      render: (kind: PayrollItemKind) =>
        kind === PayrollItemKind.Allowance
          ? translate("payrollPage.recurring.kind_allowance")
          : translate("payrollPage.recurring.kind_deduction"),
    },
    {
      title: th(translate("payrollPage.recurring.col_amount")),
      dataIndex: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount: number) => <span style={tnum}>{formatCurrency(amount)}</span>,
    },
    {
      // Icon-only, matching the edit/delete pattern used everywhere else in
      // payroll (the Lain-lain/Potongan item rows, the run table's edit
      // affordances) — and correctly labeled as Edit now, not Simpan. This
      // button never actually saved anything itself; it opens the edit
      // modal, which has its own Simpan button inside.
      //
      // Explicit narrow width — with tableLayout="fixed", an unset width
      // would give this column the same even share as the data columns,
      // leaving a wide mostly-empty cell around two small icons.
      title: "",
      key: "actions",
      width: 84,
      render: (_, record) => (
        <Flex gap={4}>
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (!hasOpenDraft) {
                showDestructiveConfirm({
                  title: translate("payrollPage.recurring.delete_confirm_title"),
                  message: translate("payrollPage.recurring.delete_confirm_content"),
                  confirmLabel: translate("payrollPage.rates.row_delete"),
                  onConfirm: () => deleteItem({ id: record.id }),
                });
                return;
              }
              // With a draft open the delete carries "Berlaku mulai" too.
              // The pick is captured in a closure the same way the class
              // page's duplicate picker captures its Select.
              let applyTo: DraftApplyChoice = "current";
              showConfirmModal({
                tone: "amber",
                destructive: true,
                title: translate("payrollPage.recurring.delete_confirm_title"),
                content: (
                  <Flex vertical gap={12}>
                    <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
                      {translate("payrollPage.recurring.delete_confirm_content")}
                    </Typography.Text>
                    <ApplyToPickerUncontrolled onChange={(v) => (applyTo = v)} />
                  </Flex>
                ),
                confirmLabel: translate("payrollPage.rates.row_delete"),
                onConfirm: () => deleteItem({ id: record.id, apply_to: applyTo }),
              });
            }}
          />
        </Flex>
      ),
    },
  ];

  return (
    <Table
      showSorterTooltip={false}
      rowKey="id"
      size="middle"
      loading={isLoading}
      columns={columns}
      dataSource={items}
      pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false }}
      onChange={handleTableChange}
      // Same as BaseSalaryTable — even column widths instead of sizing to
      // content.
      tableLayout="fixed"
    />
  );
};

// Gaji Pokok and Item Berulang are both staff-keyed tables (paged at
// PAGE_SIZE since 2026-09-17; a live studio's roster was filling the screen).
// Two full separate Cards back to back would still add real scroll height,
// so they share ONE card with two tabs instead.
// Only one table is visible at a time, costing no more vertical space than
// Item Berulang alone used to. The "+ Tambah Item" button (Item Berulang
// only) lives in tabBarExtraContent so it only appears while that tab is
// active.
// `locked`: same first-run rule as ClassesCard. Gaji Pokok would technically
// save without the payroll_setting row (it lives on staff), but the server
// guards it too so the whole tab is one rule: save the settings first.
const StaffPayrollCard = ({ locked = false }: { locked?: boolean }) => {
  const [activeTab, setActiveTab] = useState<"baseSalary" | "recurring">("baseSalary");
  const [modalState, setModalState] = useState<{ open: boolean; editing?: PayrollRecurringItem }>({ open: false });
  // Off by default, same toggle/copy as Run Payroll's and Komisi per Kelas's
  // own "Sertakan yang tidak aktif" — without it, a deactivated instructor's
  // Gaji Pokok default (and their entry in the Item Berulang staff picker)
  // becomes completely inaccessible, not just hidden from a picker: this
  // card's staff list IS the Gaji Pokok table's entire data source, with no
  // separate unfiltered view underneath it the way Item Berulang's own table
  // has (that one lists existing items regardless of this toggle).
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const { data: staff = [], isLoading: isStaffLoading } = useQuery(payrollStaffOptions(includeDeparted));
  // "Berlaku mulai" is offered only while a DRAFT run is open (a Reopened
  // run is frozen for settings, see the server's recomputeRunSessions).
  const { data: runs = [] } = useQuery(payrollRunsOptions());
  const hasOpenDraft = runs.some((r) => r.status === PayrollRunStatus.Draft);

  return (
    <Card
      id={PAYROLL_STAFF_CARD_ID}
      title={translate("payrollPage.staffCard.card_title")}
      extra={locked ? <LockedHint /> : undefined}
      styles={locked ? lockedCardStyles : { body: { padding: 18 } }}
    >
      {/* Only the Tabs subtree is disabled: tab switching itself still works
          (the card stays browsable), while the Switch, "+ Tambah Item", the
          Gaji Pokok edit trigger and the item edit/delete buttons all pick
          the context up. The form modal below stays outside on purpose: its
          openers are disabled, so it can't open while locked. */}
      <ConfigProvider componentDisabled={locked || undefined}>
        <Tabs
          // Otherwise an open Gaji Pokok edit popover isn't cleaned up when
          // switching to Item Berulang without closing it first. Inactive
          // tab panes stay mounted by default, so the popover's open state
          // just lingers. Unmounting the inactive pane resets it (and any
          // other per-tab state) on every switch.
          destroyOnHidden
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as typeof activeTab)}
          tabBarExtraContent={
            <Flex align="center" gap={16}>
              <Flex align="center" gap={8}>
                <Switch size="small" checked={includeDeparted} onChange={setIncludeDeparted} />
                <Typography.Text style={{ fontSize: textPx(13) }}>
                  {translate("payrollPage.run_start.include_departed")}
                </Typography.Text>
              </Flex>
              {activeTab === "recurring" && (
                // AntD's tab-bar underline tracks the tab LABELS' own height,
                // not the extra-content row as a whole, so this button (same
                // size/look as before) sits a few px lower than the labels and
                // visually overlaps the line. Nudged up to clear it, without
                // changing its size or style.
                <ConfigProvider theme={modalButtonsTheme}>
                  <Button
                    type="primary"
                    style={{ marginTop: -6 }}
                    onClick={() => setModalState({ open: true })}
                  >
                    {translate("payrollPage.recurring.add_button")}
                  </Button>
                </ConfigProvider>
              )}
            </Flex>
          }
          items={[
            {
              key: "baseSalary",
              label: translate("payrollPage.baseSalary.tab_label"),
              children: <BaseSalaryTable staff={staff} isLoading={isStaffLoading} locked={locked} hasOpenDraft={hasOpenDraft} />,
            },
            {
              key: "recurring",
              label: translate("payrollPage.recurring.card_title"),
              children: <RecurringItemsTable onEdit={(item) => setModalState({ open: true, editing: item })} hasOpenDraft={hasOpenDraft} />,
            },
          ]}
        />
      </ConfigProvider>
      <RecurringItemFormModal
        open={modalState.open}
        editing={modalState.editing}
        staff={staff}
        hasOpenDraft={hasOpenDraft}
        onClose={() => setModalState({ open: false })}
      />
    </Card>
  );
};

const PayrollSettingsPage = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const [guideHidden, setGuideHidden] = usePayrollGuideHidden(authState.business?.id ?? "");
  // Same queries the cards and the guide use (React Query dedupes them).
  // `locked` is only true once the server has answered exists=false:
  // undefined while loading means "not locked", so a configured studio
  // never flashes the locked state. Every downstream page and write is
  // guarded on its own (PayrollSettingsRequired / assertPayrollSettingExists).
  const { data: settings, errorUpdateCount: settingsErrors } = useQuery(payrollSettingsOptions());
  const { data: runs, errorUpdateCount: runsErrors } = useQuery(payrollRunsOptions());
  // "Has failed at least once", not isError: the card below observes the same
  // settings query, and its mount refetches a failed one, which puts isError
  // back to false; gating on isError looped unmount, refetch, fail forever.
  const settingsFailed = !settings && settingsErrors > 0;
  const runsFailed = !runs && runsErrors > 0;
  const locked = !!settings && !settings.exists;
  // The guide stays until the first payroll run exists (any status). The
  // Manager may hide it once the frequency is saved; before that it is the
  // only place to set the frequency, so it always shows (a studio with no
  // settings cannot have a run). A failed request never leaves the slot
  // loading: without settings the plain card renders as before, and without
  // runs only the unsaved case shows the guide.
  const ready = (!!settings || settingsFailed) && (!!runs || runsFailed);
  const noRunsYet = runs?.length === 0;
  const showGuide = !!settings && (!settings.exists || (noRunsYet && !guideHidden));

  return (
    <>
      <PageHeading
        title={translate("payrollPage.title")}
        extra={
          // Hidden, not disabled, before the frequency is saved: the guide's
          // last step says when payroll can be run.
          !locked && (
            <Flex gap={10}>
              {/* Plain default button, matching "Unduh Semua Slip" on the run
                  page exactly (same secondary-utility-button role next to a
                  navy primary action) — no bgSoft fill. */}
              <Button icon={<HistoryOutlined />} onClick={() => navigate(PATHS.PAYROLL_HISTORY)}>
                {translate("payrollPage.history.title")}
              </Button>
              <ConfigProvider theme={modalButtonsTheme}>
                <Button type="primary" onClick={() => navigate(PATHS.PAYROLL_RUN_NEW, { state: { from: PATHS.PAYROLL } })}>
                  {translate("payrollPage.run_start.title")}
                </Button>
              </ConfigProvider>
            </Flex>
          )
        }
      />
      <Flex vertical gap={20}>
        {!ready ? (
          <Card loading styles={{ body: { padding: 18 } }} />
        ) : showGuide ? (
          <PayrollSetupGuide onHide={() => setGuideHidden(true)} />
        ) : (
          <SettingsCard onShowGuide={noRunsYet && guideHidden ? () => setGuideHidden(false) : undefined} />
        )}
        <ClassesCard locked={locked} />
        <StaffPayrollCard locked={locked} />
      </Flex>
    </>
  );
};

export default PayrollSettingsPage;
